import React, { useEffect, useState } from 'react';
import { getGoogleOAuthProviders, saveGoogleOAuthProviders } from '../utils/api';

const newProvider = () => ({
  id: (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `google-oauth-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  email_scope: 'domain',
  domain: '',
  visibility: 'private',
  client_id: '',
  client_secret: '',
  redirect_uri: '',
  hasClientSecret: false
});

export function GoogleCredentialsPanel() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadProviders = async () => {
    setLoading(true);
    try {
      const data = await getGoogleOAuthProviders();
      setProviders((data.providers || []).map(provider => ({
        ...provider,
        domain: provider.domain || '',
        client_secret: ''
      })));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to load Google credentials' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const updateProvider = (id, field, value) => {
    setProviders(prev => prev.map(provider => (
      provider.id === id
        ? {
            ...provider,
            [field]: value
          }
        : provider
    )));
  };

  const addProvider = () => {
    setProviders(prev => [...prev, newProvider()]);
  };

  const removeProvider = (id) => {
    setProviders(prev => prev.filter(provider => provider.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await saveGoogleOAuthProviders(providers);
      setProviders((data.providers || []).map(provider => ({
        ...provider,
        domain: provider.domain || '',
        client_secret: ''
      })));
      setMessage({ type: 'success', text: 'Google credentials saved successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to save Google credentials' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-category google-credentials-panel">
      <h3>
        <i className="fab fa-google"></i> Google Credentials
        <span className="badge badge-admin">ADMIN</span>
      </h3>
      <p className="section-description">
        Store shared Google OAuth app credentials for future user setup. These values do not change the current per-user Google credential flow.
      </p>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Loading Google credentials...
        </div>
      ) : (
        <>
          <div className="google-credentials-list">
            {providers.length === 0 && (
              <div className="empty-state compact">
                <i className="fab fa-google"></i>
                <p>No Google credential providers configured</p>
              </div>
            )}

            {providers.map((provider, index) => (
              <div className="google-credential-card" key={provider.id}>
                <div className="google-credential-card-header">
                  <h4>OAuth Provider {index + 1}</h4>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => removeProvider(provider.id)}
                    disabled={saving}
                  >
                    <i className="fas fa-trash"></i> Remove
                  </button>
                </div>

                <div className="google-credential-grid">
                  <div className="form-group">
                    <label htmlFor={`google-provider-name-${provider.id}`}>Name</label>
                    <input
                      id={`google-provider-name-${provider.id}`}
                      type="text"
                      value={provider.name}
                      onChange={(e) => updateProvider(provider.id, 'name', e.target.value)}
                      placeholder="e.g. Production Google OAuth"
                      disabled={saving}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`google-provider-email-scope-${provider.id}`}>Email Addresses</label>
                    <select
                      id={`google-provider-email-scope-${provider.id}`}
                      value={provider.email_scope}
                      onChange={(e) => updateProvider(provider.id, 'email_scope', e.target.value)}
                      disabled={saving}
                    >
                      <option value="domain">Only this domain</option>
                      <option value="all">Every email address</option>
                    </select>
                  </div>

                  {provider.email_scope === 'domain' && (
                    <div className="form-group">
                      <label htmlFor={`google-provider-domain-${provider.id}`}>Domain</label>
                      <input
                        id={`google-provider-domain-${provider.id}`}
                        type="text"
                        value={provider.domain || ''}
                        onChange={(e) => updateProvider(provider.id, 'domain', e.target.value)}
                        placeholder="customer-domain.com"
                        disabled={saving}
                        autoComplete="off"
                        spellCheck={false}
                      />
                      <div className="form-hint">Enter the user email domain this provider applies to.</div>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor={`google-provider-visibility-${provider.id}`}>Visibility</label>
                    <select
                      id={`google-provider-visibility-${provider.id}`}
                      value={provider.visibility}
                      onChange={(e) => updateProvider(provider.id, 'visibility', e.target.value)}
                      disabled={saving}
                    >
                      <option value="private">Private</option>
                      <option value="public">Public</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`google-provider-client-id-${provider.id}`}>OAuth client identifier</label>
                    <input
                      id={`google-provider-client-id-${provider.id}`}
                      type="text"
                      value={provider.client_id}
                      onChange={(e) => updateProvider(provider.id, 'client_id', e.target.value)}
                      placeholder="123456789-...apps.googleusercontent.com"
                      disabled={saving}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`google-provider-client-secret-${provider.id}`}>OAuth client secret</label>
                    <input
                      id={`google-provider-client-secret-${provider.id}`}
                      type="text"
                      value={provider.client_secret}
                      onChange={(e) => updateProvider(provider.id, 'client_secret', e.target.value)}
                      placeholder={provider.hasClientSecret ? 'Leave blank to keep current secret' : 'Paste client secret'}
                      disabled={saving}
                      autoComplete="off"
                      spellCheck={false}
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                    />
                    {provider.hasClientSecret && (
                      <div className="form-hint">A secret is saved. It is write-only from this page.</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor={`google-provider-redirect-uri-${provider.id}`}>OAuth redirect URI</label>
                    <input
                      id={`google-provider-redirect-uri-${provider.id}`}
                      type="url"
                      value={provider.redirect_uri}
                      onChange={(e) => updateProvider(provider.id, 'redirect_uri', e.target.value)}
                      placeholder="https://example.com/casc/v1/api/google/auth/callback"
                      disabled={saving}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="google-credentials-actions">
            <button type="button" className="btn btn-secondary" onClick={addProvider} disabled={saving}>
              <i className="fas fa-plus"></i> Add Provider
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Save Google Credentials
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
